/**
 * Consent Version Registry
 *
 * This module defines all consent types and their versions with effective date ranges.
 * Each version specifies which fields it captures and when it's valid.
 * The resolver functions determine which version is active at any given point in time.
 *
 * Usage:
 *   const version = getConsentVersionAtDate('ACUPUNCTURE', '2017-05-14');
 *   const fields = getAllConsentFieldsForVersion('ACUPUNCTURE', '1.0');
 */

// ============================================================================
// Consent Type Constants
// ============================================================================

export const CONSENT_TYPES = {
  ACUPUNCTURE: "ACUPUNCTURE",
  OSTEOPATHY: "OSTEOPATHY",
  MASSAGE: "MASSAGE",
  TELUS: "TELUS",
  SENSITIVE_AREAS: "SENSITIVE_AREAS",
  REIKI: "REIKI",
  NATUROPATHIC: "NATUROPATHIC",
  PHYSIOTHERAPY: "PHYSIOTHERAPY",
  CHIROPRACTOR: "CHIROPRACTOR",
  HOMEOPATHIC: "HOMEOPATHIC",
  GENERAL: "GENERAL",
};

// ============================================================================
// Version Registry
// ============================================================================

/**
 * Defines all versions of all consent types with their effective date ranges.
 * Format:
 * {
 *   CONSENT_TYPE: [
 *     {
 *       versionId: '1.0',
 *       validFrom: '2017-01-01',
 *       validTo: null,  // null = currently active
 *       fields: [...],  // field names that this version captures
 *       checkboxFields: [...],  // boolean fields
 *       textFields: [...],      // text/textarea fields
 *     },
 *     // Future versions can be added here with validFrom and validTo dates
 *   ]
 * }
 */
export const CONSENT_VERSIONS = {
  [CONSENT_TYPES.ACUPUNCTURE]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: [
        "consentAcupuncture",
        "consentBleedEasily",
        "consentPregnant",
        "consentHIV",
        "consentPacemaker",
        "consentMetal",
        "sigAcu",
      ],
      checkboxFields: [
        "consentAcupuncture",
        "consentBleedEasily",
        "consentPregnant",
        "consentHIV",
        "consentPacemaker",
        "consentMetal",
      ],
      textFields: [],
      signatureField: "sigAcu",
    },
  ],

  [CONSENT_TYPES.OSTEOPATHY]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: "2026-05-17",
      fields: ["consentOsteopathy", "sigOsteo"],
      checkboxFields: ["consentOsteopathy"],
      textFields: [],
      signatureField: "sigOsteo",
    },
    {
      versionId: "2.0",
      validFrom: "2026-05-18",
      validTo: null,
      fields: [
        "consentOsteopathy",
        "osteo_temporarySoreness",
        "osteo_pressure",
        "osteo_strenuousActivity",
        "osteo_communicateDiscomfort",
        "osteo_noSpecificOutcome",
        "sigOsteo",
      ],
      checkboxFields: [
        "consentOsteopathy",
        "osteo_temporarySoreness",
        "osteo_pressure",
        "osteo_strenuousActivity",
        "osteo_communicateDiscomfort",
        "osteo_noSpecificOutcome",
      ],
      textFields: [],
      signatureField: "sigOsteo",
    },
  ],

  [CONSENT_TYPES.MASSAGE]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: "2026-05-17",
      fields: ["consentMassage", "sigMassage"],
      checkboxFields: ["consentMassage"],
      textFields: [],
      signatureField: "sigMassage",
    },
    {
      versionId: "2.0",
      validFrom: "2026-05-18",
      validTo: null,
      fields: [
        "consentMassage",
        "massage_postTreatmentSoreness",
        "massage_deepTissue",
        "massage_strenuousActivity",
        "massage_communicateDiscomfort",
        "massage_noSpecificOutcome",
        "sigMassage",
      ],
      checkboxFields: [
        "consentMassage",
        "massage_postTreatmentSoreness",
        "massage_deepTissue",
        "massage_strenuousActivity",
        "massage_communicateDiscomfort",
        "massage_noSpecificOutcome",
      ],
      textFields: [],
      signatureField: "sigMassage",
    },
  ],

  [CONSENT_TYPES.TELUS]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: ["consentTelus", "consentAcceptTerms", "sigTelus"],
      checkboxFields: ["consentTelus", "consentAcceptTerms"],
      textFields: [],
      signatureField: "sigTelus",
    },
  ],

  [CONSENT_TYPES.SENSITIVE_AREAS]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: ["consentSensitive", "sigSensitive"],
      checkboxFields: ["consentSensitive"],
      textFields: [],
      signatureField: "sigSensitive",
    },
  ],

  [CONSENT_TYPES.REIKI]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: [
        "consentReiki",
        "reikiConsentAndWaiver",
        "reikiTestimonialDisclaimer",
        "reikiAssumptionOfRisk",
        "reikiGoverningLaw",
        "reikiExclusiveJurisdiction",
        "sigReiki",
      ],
      checkboxFields: [
        "consentReiki",
        "reikiConsentAndWaiver",
        "reikiTestimonialDisclaimer",
        "reikiAssumptionOfRisk",
        "reikiGoverningLaw",
        "reikiExclusiveJurisdiction",
      ],
      textFields: [],
      signatureField: "sigReiki",
    },
  ],

  [CONSENT_TYPES.NATUROPATHIC]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: [
        "consentNaturopathic",
        "currentHealthCareProviders",
        "describeYourHealthConcerns",
        "currentSupplements",
        "listFamilyHistory",
        "Diet_Restrictions",
        "Diet_AdditionalComments",
        "Diet_RateYourEnergy",
        "Diet_RateYourStress",
        "Diet_BiggestSourceOfStress",
        "Exercise_ActivityLevel",
        "Exercise_TypeofActivity",
        "Exercise_OtherRelevantInfo",
        "sigNaturopathic",
      ],
      checkboxFields: ["consentNaturopathic"],
      textFields: [
        "currentHealthCareProviders",
        "describeYourHealthConcerns",
        "currentSupplements",
        "listFamilyHistory",
        "Diet_Restrictions",
        "Diet_AdditionalComments",
        "Diet_RateYourEnergy",
        "Diet_RateYourStress",
        "Diet_BiggestSourceOfStress",
        "Exercise_ActivityLevel",
        "Exercise_TypeofActivity",
        "Exercise_OtherRelevantInfo",
      ],
      signatureField: "sigNaturopathic",
    },
  ],

  [CONSENT_TYPES.PHYSIOTHERAPY]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: ["consentPhysiotherapy"],
      checkboxFields: ["consentPhysiotherapy"],
      textFields: [],
      signatureField: null,
    },
  ],

  [CONSENT_TYPES.CHIROPRACTOR]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: ["consentChiropractor"],
      checkboxFields: ["consentChiropractor"],
      textFields: [],
      signatureField: null,
    },
  ],

  [CONSENT_TYPES.HOMEOPATHIC]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: ["consentHomeopathic"],
      checkboxFields: ["consentHomeopathic"],
      textFields: [],
      signatureField: null,
    },
  ],

  [CONSENT_TYPES.GENERAL]: [
    {
      versionId: "1.0",
      validFrom: "2017-01-01",
      validTo: null,
      fields: [
        "Accuracy_IAgree",
        "CancellationPolicy_IAgree",
        "EmailCommunication_IAgree",
        "PrivacyPolicy_IAgree",
        "IWillBeSigning",
        "substituteSignature",
        "substituteSignatureName",
        "sigGeneral",
      ],
      checkboxFields: [
        "Accuracy_IAgree",
        "CancellationPolicy_IAgree",
        "EmailCommunication_IAgree",
        "PrivacyPolicy_IAgree",
        "IWillBeSigning",
        "substituteSignature",
      ],
      textFields: ["substituteSignatureName"],
      signatureField: "sigGeneral",
    },
  ],
};

// ============================================================================
// Resolver Functions
// ============================================================================

/**
 * Get the active consent version for a specific consent type at a given date.
 * Returns the version object that was valid on the specified date.
 *
 * @param {string} consentType - One of the CONSENT_TYPES constants
 * @param {string|Date} dateOrString - ISO date string or Date object (e.g., '2017-05-14')
 * @returns {Object|null} The active version object, or null if not found
 *
 * @example
 *   const version = getConsentVersionAtDate('ACUPUNCTURE', '2017-05-14');
 *   console.log(version.versionId); // '1.0'
 */
export function getConsentVersionAtDate(consentType, dateOrString) {
  if (!CONSENT_VERSIONS[consentType]) {
    console.warn(`Unknown consent type: ${consentType}`);
    return null;
  }

  // Convert date to ISO string for comparison
  let dateString;
  if (dateOrString instanceof Date) {
    dateString = dateOrString.toISOString().split("T")[0];
  } else if (typeof dateOrString === "string") {
    dateString = dateOrString;
  } else {
    console.warn(`Invalid date type: ${typeof dateOrString}`);
    return null;
  }

  // Find the version that was active on this date
  const versions = CONSENT_VERSIONS[consentType];
  const activeVersion = versions.find((version) => {
    const isAfterStart = dateString >= version.validFrom;
    const isBeforeEnd = !version.validTo || dateString <= version.validTo;
    return isAfterStart && isBeforeEnd;
  });

  return activeVersion || null;
}

/**
 * Get all field names for a specific consent type and version.
 * Useful for extracting which fields to capture when submitting a form.
 *
 * @param {string} consentType - One of the CONSENT_TYPES constants
 * @param {string} versionId - Version ID (e.g., '1.0')
 * @returns {Array<string>} List of field names
 *
 * @example
 *   const fields = getAllConsentFieldsForVersion('ACUPUNCTURE', '1.0');
 *   // ['consentAcupuncture', 'consentBleedEasily', ..., 'sigAcu']
 */
export function getAllConsentFieldsForVersion(consentType, versionId) {
  if (!CONSENT_VERSIONS[consentType]) {
    console.warn(`Unknown consent type: ${consentType}`);
    return [];
  }

  const version = CONSENT_VERSIONS[consentType].find(
    (v) => v.versionId === versionId,
  );
  return version ? version.fields : [];
}

/**
 * Get the current active version for a consent type (today's date).
 * Convenience function that uses the current date.
 *
 * @param {string} consentType - One of the CONSENT_TYPES constants
 * @returns {Object|null} The active version object
 */
export function getCurrentConsentVersion(consentType) {
  const today = new Date().toISOString().split("T")[0];
  return getConsentVersionAtDate(consentType, today);
}

/**
 * Get all available versions for a consent type.
 * Useful for admin interfaces that need to show version history.
 *
 * @param {string} consentType - One of the CONSENT_TYPES constants
 * @returns {Array<Object>} List of all versions for this type
 */
export function getAllVersionsForConsentType(consentType) {
  return CONSENT_VERSIONS[consentType] || [];
}

/**
 * Get the version that will be active on a future date.
 * Useful for previewing upcoming consent form changes.
 *
 * @param {string} consentType - One of the CONSENT_TYPES constants
 * @param {string|Date} dateOrString - ISO date string or Date object
 * @returns {Object|null} The upcoming or current version object
 */
export function getUpcomingOrCurrentVersion(consentType, dateOrString) {
  return getConsentVersionAtDate(consentType, dateOrString);
}
